import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { schoolService } from '../services/schoolService';
import { School, SchoolRequest } from '../types';
import { PlusIcon, SearchIcon, SchoolIcon, CalendarIcon } from '../components/Icons';

const SchoolsPage: React.FC = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState<School | null>(null);
  const [formData, setFormData] = useState<SchoolRequest>({
    name: '',
    address: '',
    phone: '',
    email: '',
    description: '',
    showStudentDetails: true,
  });

  // 全局学年选择器
  const currentYear = new Date().getFullYear().toString();
  const [selectedYear, setSelectedYear] = useState<string>(localStorage.getItem('selectedAcademicYear') || currentYear);
  const [availableYears, setAvailableYears] = useState<string[]>([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchSchools();
    generateAvailableYears();
  }, [currentPage, searchTerm]);

  // 当学年改变时，保存到 localStorage
  useEffect(() => {
    localStorage.setItem('selectedAcademicYear', selectedYear);
  }, [selectedYear]);

  const generateAvailableYears = () => {
    const thisYear = new Date().getFullYear();
    const years: string[] = [];
    for (let i = -2; i <= 2; i++) {
      years.push((thisYear + i).toString());
    }
    setAvailableYears(years);
  };

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const response = await schoolService.getSchools({
        page: currentPage,
        size: 10,
        search: searchTerm || undefined,
      });
      setSchools(response.content);
      setTotalPages(response.totalPages);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || '获取学校列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSchool(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      description: '',
      showStudentDetails: true,
    });
    setShowModal(true);
  };

  const handleEdit = (school: School) => {
    setEditingSchool(school);
    setFormData({
      name: school.name,
      address: school.address || '',
      phone: school.phone || '',
      email: school.email || '',
      description: school.description || '',
      showStudentDetails: school.showStudentDetails !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这所学校吗？')) return;

    try {
      await schoolService.deleteSchool(id);
      fetchSchools();
    } catch (err: any) {
      alert(err.response?.data?.message || '删除学校失败');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingSchool) {
        await schoolService.updateSchool(editingSchool.id, formData);
      } else {
        await schoolService.createSchool(formData);
      }
      setShowModal(false);
      fetchSchools();
    } catch (err: any) {
      alert(err.response?.data?.message || '保存学校失败');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 点击学校行，跳转到该学校选中学年的班级列表
  const handleSchoolClick = (school: School) => {
    navigate(`/schools/${school.id}/years/${selectedYear}/classes`);
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-headline text-on-surface">学校管理</h1>
            <p className="text-sm font-body text-on-surface-variant mt-1">管理系统中的所有学校</p>
          </div>
          <div className="flex items-center gap-3">
            {/* 全局学年选择器 */}
            <div className="flex items-center gap-2 px-4 py-2 bg-surface-container-lowest shadow-soft rounded-xl">
              <CalendarIcon size={18} className="text-on-surface-variant" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="bg-transparent border-none focus:outline-none text-sm font-medium font-label text-on-surface cursor-pointer"
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year} 学年
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleCreate}
              className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl hover:opacity-90 transition-colors font-medium shadow-teal flex items-center space-x-2"
            >
              <PlusIcon size={18} />
              <span>添加学校</span>
            </button>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 rounded-2xl shadow-soft p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <CalendarIcon size={20} className="text-blue-600 mt-0.5" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-body text-blue-800">
              当前查看 <span className="font-semibold">{selectedYear} 学年</span> 的数据。点击学校名称将直接进入该学年的班级列表。
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-surface-container-lowest rounded-2xl shadow-soft p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索学校名称..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
              <div className="absolute left-3 top-3 text-on-surface-variant">
                <SearchIcon size={18} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-danger-50 text-danger-800 px-4 py-3 rounded-2xl shadow-soft mb-6">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="text-center py-12 font-body text-on-surface-variant">加载中...</div>
        ) : schools.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-on-surface-variant mb-4 flex justify-center opacity-40">
              <SchoolIcon size={48} />
            </div>
            <p className="font-body text-on-surface-variant">暂无学校数据</p>
          </div>
        ) : (
          <>
            <table className="min-w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead className="bg-surface-container-low">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium font-label text-on-surface-variant uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium font-label text-on-surface-variant uppercase tracking-wider">
                    学校名称
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium font-label text-on-surface-variant uppercase tracking-wider">
                    地址
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium font-label text-on-surface-variant uppercase tracking-wider">
                    联系方式
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium font-label text-on-surface-variant uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-container-lowest">
                {schools.map((school, index) => (
                  <tr
                    key={school.id}
                    className="hover:bg-surface-container-low transition-colors cursor-pointer"
                    onClick={() => handleSchoolClick(school)}
                    style={index < schools.length - 1 ? { borderBottom: '1px solid rgba(171, 179, 183, 0.2)' } : undefined}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-label text-on-surface-variant">#{school.id}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600 font-medium mr-3">
                          {school.name.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium font-body text-on-surface">{school.name}</div>
                          <div className="text-xs font-label text-on-surface-variant">点击查看 {selectedYear} 学年班级</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-body text-on-surface-variant">{school.address || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-body text-on-surface-variant">{school.phone || '-'}</div>
                      <div className="text-sm font-label text-on-surface-variant">{school.email || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(school);
                        }}
                        className="text-primary-600 hover:text-primary-900 font-medium mr-4"
                      >
                        编辑
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(school.id);
                        }}
                        className="text-danger-600 hover:text-danger-900 font-medium"
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="bg-surface-container-low px-6 py-4 flex items-center justify-between" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
                <div className="text-sm font-label text-on-surface-variant">
                  共 {totalPages} 页
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 bg-surface-container-high rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-low transition-colors text-sm font-medium font-label"
                  >
                    上一页
                  </button>
                  <div className="px-4 py-2 bg-surface-container-lowest shadow-soft rounded-xl text-sm font-medium font-label">
                    {currentPage + 1} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-4 py-2 bg-surface-container-high rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-low transition-colors text-sm font-medium font-label"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-md w-full shadow-2xl">
            <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <h2 className="text-xl font-semibold font-headline text-on-surface">
                {editingSchool ? '编辑学校' : '创建学校'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium font-label text-on-surface mb-2">
                    学校名称 <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入学校名称"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium font-label text-on-surface mb-2">
                    地址
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入地址"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium font-label text-on-surface mb-2">
                    电话
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入电话"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium font-label text-on-surface mb-2">
                    邮箱
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="请输入邮箱"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium font-label text-on-surface mb-2">
                    描述
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 resize-none"
                    placeholder="请输入描述"
                  />
                </div>
                <div className="bg-surface-container-low rounded-2xl p-4">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.showStudentDetails !== false}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, showStudentDetails: e.target.checked }))
                      }
                      className="mt-1 h-4 w-4 rounded border-on-surface-variant text-primary-600 focus:ring-primary-200"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium font-label text-on-surface">
                        显示学生敏感信息
                      </div>
                      <div className="text-xs font-body text-on-surface-variant mt-1">
                        关闭后，本校学生在详情页将隐藏身份证号、学号、电话等敏感字段。默认开启。
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6 pt-6" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium font-label"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl hover:opacity-90 transition-colors font-medium shadow-teal"
                >
                  {editingSchool ? '更新' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolsPage;
